import { useState } from 'react'
import { useSupabaseQuery, useSupabaseMutation } from '../../hooks/useSupabase'
import Modal from '../shared/Modal'
import AccountForm from './AccountForm'

export default function AccountsTab({ selectedOrganization, selectedAccount, onSelectAccount }) {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingAccount, setEditingAccount] = useState(null)
  const [deletingAccount, setDeletingAccount] = useState(null)

  const { data: accounts, loading, refetch } = useSupabaseQuery('OTPAccounts', {
    eq: {
      IsDeleted: false,
      ...(selectedOrganization && { OrganisationId: selectedOrganization.OrganisationId })
    },
    order: { column: 'Name', ascending: true }
  })

  const { insert, update, softDelete, loading: mutating } = useSupabaseMutation('OTPAccounts')

  const handleCreate = async (formData) => {
    const { error } = await insert({
      ...formData,
      OrganisationId: selectedOrganization.OrganisationId
    })
    if (error) {
      alert(`Failed to create account: ${error.message}`)
    } else {
      setShowCreateModal(false)
      refetch()
    }
  }

  const handleUpdate = async (formData) => {
    const { error } = await update(editingAccount.AccountId, formData, 'AccountId')
    if (!error) {
      setEditingAccount(null)
      refetch()
      if (selectedAccount?.AccountId === editingAccount.AccountId) {
        onSelectAccount({ ...selectedAccount, ...formData })
      }
    }
  }

  const handleDelete = async () => {
    const { error } = await softDelete(deletingAccount.AccountId, 'AccountId')
    if (!error) {
      setDeletingAccount(null)
      refetch()
      if (selectedAccount?.AccountId === deletingAccount.AccountId) {
        onSelectAccount(null)
      }
    }
  }

  if (!selectedOrganization) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No Organization Selected</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Please select an organization from the Organizations tab first
        </p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">OTP Accounts</h2>
          <p className="text-gray-600 dark:text-gray-400">Accounts for: {selectedOrganization.Name}</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>New Account</span>
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Code</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Expiry</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Cooldown</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {accounts?.map((account) => {
              const isSelected = selectedAccount?.AccountId === account.AccountId
              return (
                <tr
                  key={account.AccountId}
                  onClick={() => onSelectAccount(account)}
                  className={`cursor-pointer transition-colors ${
                    isSelected ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {isSelected && (
                        <svg className="w-5 h-5 text-primary-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{account.Name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">ID: {account.AccountId}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {account.DefaultLength} digits
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {account.DefaultExpiry} min
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {account.ResendCooldownSeconds}s
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingAccount(account) }}
                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                    >
                      Edit
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setDeletingAccount(account) }}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>

        {(!accounts || accounts.length === 0) && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No accounts</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating a new OTP account.</p>
          </div>
        )}
      </div>

      {showCreateModal && (
        <Modal title="Create OTP Account" onClose={() => setShowCreateModal(false)} size="lg">
          <AccountForm
            onSubmit={handleCreate}
            onCancel={() => setShowCreateModal(false)}
            loading={mutating}
          />
        </Modal>
      )}

      {editingAccount && (
        <Modal title="Edit OTP Account" onClose={() => setEditingAccount(null)} size="lg">
          <AccountForm
            account={editingAccount}
            onSubmit={handleUpdate}
            onCancel={() => setEditingAccount(null)}
            loading={mutating}
          />
        </Modal>
      )}

      {deletingAccount && (
        <Modal title="Delete OTP Account" onClose={() => setDeletingAccount(null)} size="sm">
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">
              Are you sure you want to delete <strong>{deletingAccount.Name}</strong>?
              This will affect all associated API credentials and OTP history.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeletingAccount(null)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={mutating}
                className="px-4 py-2 text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {mutating ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
